import type { FinishOptions } from '@base/shared';
import { useTranslation } from 'react-i18next';
import { useActiveFinish } from '../../platform.tsx';
import { niceScale } from './logic.ts';

export interface ChartPoint {
  label: string;
  value: number;
}

/**
 * Gráfico simples de uma série (ADR 0128), em SVG com as cores do tema: barras, linha ou área,
 * como o acabamento (`chartStyle`) mandar. Os números exatos ficam numa tabela ao lado ou embaixo
 * (`ValueTable`); o gráfico tem o resumo para leitores de tela.
 */
export function MiniChart({
  points,
  label,
  format = (n) => n.toLocaleString(),
  kind,
}: {
  points: readonly ChartPoint[];
  /** O que o gráfico mostra ("Vendas por mês"). */
  label: string;
  format?: (n: number) => string;
  /** Força um tipo; sem ele, o do acabamento. */
  kind?: FinishOptions['chartStyle'];
}) {
  const { t } = useTranslation();
  const finish = useActiveFinish();
  const style = kind ?? finish.chartStyle;
  const W = 600;
  const H = 200;
  const pad = { l: 44, r: 8, t: 8, b: 24 };
  const { top, marks } = niceScale(Math.max(0, ...points.map((p) => p.value)));
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const slot = iw / Math.max(1, points.length);
  const x = (i: number) => pad.l + slot * i + slot / 2;
  const y = (v: number) => pad.t + ih - (Math.max(0, v) / top) * ih;
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.value)}`).join(' ');
  const area = `${line} L${x(points.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
  const summary = t('patterns.chart.summary', {
    label,
    items: points.map((p) => `${p.label}: ${format(p.value)}`).join('; '),
  });
  return (
    <figure className="flex flex-col gap-2">
      <figcaption className="text-sm font-medium">{label}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary} className="h-auto w-full">
        {marks.map((m) => (
          <g key={m}>
            <line x1={pad.l} x2={W - pad.r} y1={y(m)} y2={y(m)} className="stroke-rule" />
            <text
              x={pad.l - 6}
              y={y(m)}
              dy="0.32em"
              textAnchor="end"
              className="fill-muted text-[11px] tabular-nums"
            >
              {format(m)}
            </text>
          </g>
        ))}
        {style === 'bar' &&
          points.map((p, i) => (
            <rect
              key={p.label}
              x={x(i) - slot * 0.3}
              width={slot * 0.6}
              y={y(p.value)}
              height={Math.max(0, y(0) - y(p.value))}
              rx={3}
              className="fill-accent"
            />
          ))}
        {style === 'area' && points.length > 0 && (
          <path d={area} className="fill-accent opacity-20" />
        )}
        {style !== 'bar' && points.length > 0 && (
          <>
            <path d={line} fill="none" strokeWidth={2.5} className="stroke-accent" />
            {points.map((p, i) => (
              <circle key={p.label} cx={x(i)} cy={y(p.value)} r={3.5} className="fill-accent" />
            ))}
          </>
        )}
        {points.map((p, i) => (
          <text
            key={p.label}
            x={x(i)}
            y={H - 6}
            textAnchor="middle"
            className="fill-muted text-[11px]"
          >
            {p.label}
          </text>
        ))}
      </svg>
    </figure>
  );
}
